import { Injectable } from '@angular/core';
import { SelectItemV2, UnitsV2 } from '@schenck/rotec-components';
import {
  QualityGradeTypes,
  ToleranceModes,
  UnbalanceReadout,
} from 'src/utils/enums/rotor-type.enum';
import { Adapter } from 'src/utils/interfaces/adapter-service.interfaces';
import {
  RotorTypeFormsData,
  ToleranceIsoCalculationFormData,
} from 'src/utils/interfaces/rotor-type-forms-data.interfaces';
import {
  IsoTolerance,
  PlaneTolerance,
  RotorTypeValue,
  RotorTypeValueV2,
  Tolerance,
} from 'src/utils/interfaces/rotor-type.interfaces';
import { mapToRotorTypeValue, mapToRotorTypeValueV2 } from './adapter.mappers';

export class StaticToleranceModel implements Tolerance {
  constructor(
    public readonly staticTolerance: RotorTypeValue,
    public readonly staticToleranceUnitFamily: RotorTypeValueV2,
    public readonly toleranceMode: RotorTypeValueV2,
    public readonly iso21940Tolerance?: IsoTolerance
  ) {}
}

export class DynamicToleranceModel implements Tolerance {
  constructor(
    public readonly dynamicPlaneTolerances: PlaneTolerance[],
    public readonly dynamicToleranceUnitFamily: RotorTypeValueV2,
    public readonly toleranceMode: RotorTypeValueV2,
    public readonly iso21940Tolerance?: IsoTolerance
  ) {}
}

@Injectable({
  providedIn: 'root',
})
export class ToleranceAdapter implements Adapter<RotorTypeFormsData, Tolerance> {
  /**
   * Creates the adapted form data for tolerance field
   *
   * @param formData: RotorTypeFormsData
   * @param units UnitsV2[]
   * @returns Tolerance
   */
  public adapt(
    { tolerancesStep, rotorTypeSettingsStep }: RotorTypeFormsData,
    units: UnitsV2[]
  ): Tolerance {
    const unbalanceReadout = rotorTypeSettingsStep.unbalanceReadout;
    const isUserDefineMode = tolerancesStep.toleranceMode.value === ToleranceModes.Manual;
    const isoTolerancesMode = tolerancesStep.toleranceMode.value === ToleranceModes.Iso21940;
    const isStatic =
      unbalanceReadout === UnbalanceReadout.Static ||
      unbalanceReadout === UnbalanceReadout.StaticCouple ||
      unbalanceReadout === UnbalanceReadout.StaticDynamic;
    const isDynamic =
      unbalanceReadout === UnbalanceReadout.Dynamic ||
      unbalanceReadout === UnbalanceReadout.StaticDynamic;
    const isCouple = unbalanceReadout === UnbalanceReadout.StaticCouple;

    const chosenModeData = isUserDefineMode
      ? tolerancesStep.userDefined
      : tolerancesStep.isoCalculation;

    const userDefinedTolerances: SelectItemV2[] = [
      chosenModeData.dynamicTolerance1,
      chosenModeData.dynamicTolerance2,
    ];

    const staticTolerance: RotorTypeValue = mapToRotorTypeValue(
      chosenModeData.staticTolerance,
      units
    );

    let coupleModel;

    if (isCouple) {
      const coupleTolerance: RotorTypeValue = mapToRotorTypeValue(
        chosenModeData.coupleTolerance,
        units
      );

      coupleModel = {
        coupleTolerance,
        coupleToleranceUnitFamily: mapToRotorTypeValueV2(chosenModeData.selectedCoupleUnit, true),
      };
    }

    const isoTolerances: PlaneTolerance[] = [
      {
        planeIndex: 0,
        tolerance: mapToRotorTypeValue(
          {
            name: 'dynamicTolerance1',
            value: chosenModeData.dynamicTolerance1.value,
            unit: tolerancesStep.isoCalculation?.selectedUnbalanceUnit?.value?.toString(),
          },
          units
        ),
      },
      {
        planeIndex: 1,
        tolerance: mapToRotorTypeValue(
          {
            name: 'dynamicTolerance2',
            value: chosenModeData.dynamicTolerance2.value,
            unit: tolerancesStep?.isoCalculation?.selectedUnbalanceUnit?.value?.toString(),
          },
          units
        ),
      },
    ];

    const staticModel = {
      staticTolerance,
      staticToleranceUnitFamily: mapToRotorTypeValueV2(chosenModeData.selectedStaticUnit, true),
    };

    const dynamicModel = {
      dynamicPlaneTolerances: isUserDefineMode
        ? userDefinedTolerances.map((tolerance: SelectItemV2, index: number) => ({
            planeIndex: index,
            tolerance: mapToRotorTypeValue({ ...tolerance, value: tolerance.value || 0 }, units),
          }))
        : isoTolerances,
      dynamicToleranceUnitFamily: mapToRotorTypeValueV2(chosenModeData.selectedDynamicUnit, true),
    };

    return {
      ...(isStatic && staticModel),
      ...(isDynamic && dynamicModel),
      ...(isCouple && coupleModel),
      ...(!isUserDefineMode && {
        iso21940Tolerance: this.setIsoToleranceData(
          chosenModeData as ToleranceIsoCalculationFormData,
          units
        ),
      }),
      toleranceMode: mapToRotorTypeValueV2(tolerancesStep.toleranceMode),
      ...(isUserDefineMode && {
        selectedTolerancePlane: mapToRotorTypeValueV2(
          tolerancesStep.userDefined.selectedTolerancePlane
        ),
      }),
      ...(isoTolerancesMode && {
        selectedTolerancePlane: mapToRotorTypeValueV2(
          tolerancesStep.isoCalculation.selectedTolerancePlane
        ),
      }),
    };
  }

  public setIsoToleranceData(
    data: ToleranceIsoCalculationFormData,
    units: UnitsV2[]
  ): IsoTolerance {
    const centerGravities = {
      ...(data?.centerGravity1 && {
        offsetBearing1ToCenterGravity: mapToRotorTypeValue(data?.centerGravity1, units),
      }),
      ...(data?.centerGravity2 && {
        offsetBearing2ToCenterGravity: mapToRotorTypeValue(data?.centerGravity2, units),
      }),
    };
    const qualityGrade = Object.values(QualityGradeTypes).findIndex(
      (i) => i === data.balancingQualityGrade.value
    );
    const customQualityGrade = Object.keys(QualityGradeTypes).length;

    return {
      quality: {
        name: data.balancingQualityGrade.name,
        value: +`${data.balancingQualityGrade.value}`
          .replace(',', '.')
          .replace(/[^\d.-]/g, '') as any,
        isEdited: true,
      },
      qualityGrade: {
        name: 'qualityGrade',
        value: qualityGrade !== -1 ? qualityGrade : customQualityGrade,
        isEdited: true,
      },
      operationRotationSpeed: mapToRotorTypeValue(data?.nominalSpeed, units),
      rotorMass: mapToRotorTypeValue(data?.rotorMass, units),
      deviation: {
        name: 'deviation',
        value: data.deviation,
        isEdited: true,
      },
      ...centerGravities,
    };
  }
}
